/**
 * New Tab Mode Handler
 * Utilities for handling verification completion in new-tab mode
 */

import { logger } from './logger';
import { sessionStateManager, getSessionState } from './SessionStateManager';
import { runtimeConfig } from '../services/RuntimeConfigService';
import { type FlowMode, getStoredFlowMode, storeFlowMode } from './flowMode';
import { getDevBrandSlug } from '../contexts/DevBrandContext';

const newTabLogger = logger.clone({ labels: { component: 'NewTabHandler' }});

/**
 * Check if we're running in new-tab mode
 */
export function isNewTabMode(): boolean {
  return resolveFlowMode() === 'new-tab';
}

/**
 * Resolve flow mode.
 *
 * Order is deliberate:
 *   1. Explicit `?mode=` URL param — caller (SDK or merchant) said so.
 *   2. Stored mode — preserves the original SDK-selected mode after React
 *      Router drops search params.
 *   3. `window.opener` heuristic — legacy fallback for popups without
 *      explicit signaling.
 *   4. Default to 'redirect'.
 */
export function resolveFlowMode(): FlowMode {
  const urlParams = new URLSearchParams(window.location.search);
  const modeParam = urlParams.get('mode');
  if (modeParam === 'new-tab' || modeParam === 'redirect') {
    storeFlowMode(modeParam);
    return modeParam;
  }
  const storedMode = getStoredFlowMode();
  if (storedMode) {
    return storedMode;
  }
  if (window.opener) {
    storeFlowMode('new-tab');
    return 'new-tab';
  }
  storeFlowMode('redirect');
  return 'redirect';
}

const NEW_TAB_RESULT_SENT_PREFIX = 'safepassage_new_tab_result_sent:';
let lastResultSessionId: string | null = null;

/** Returns whether the attempt marker will survive reload. */
export function markNewTabResultSent(sessionId: string): boolean {
  lastResultSessionId = sessionId;
  try {
    sessionStorage.setItem(`${NEW_TAB_RESULT_SENT_PREFIX}${sessionId}`, 'true');
    return sessionStorage.getItem(`${NEW_TAB_RESULT_SENT_PREFIX}${sessionId}`) === 'true';
  } catch {
    // Ignore storage failures - in-memory flag still prevents duplicates
    return false;
  }
}

export function hasNewTabResultSent(sessionId: string): boolean {
  if (lastResultSessionId === sessionId) {
    return true;
  }
  try {
    return sessionStorage.getItem(`${NEW_TAB_RESULT_SENT_PREFIX}${sessionId}`) === 'true';
  } catch {
    return false;
  }
}

/**
 * Send verification result to parent window via PostMessage
 */
export function sendVerificationResult(
  sessionId: string | undefined,
  status: 'verified' | 'failed' | 'cancelled',
  externalUserId?: string
): boolean {
  if (!sessionId) {
    newTabLogger.warn('No sessionId available for PostMessage');
    return false;
  }

  if (hasNewTabResultSent(sessionId)) {
    newTabLogger.info('Skipping duplicate PostMessage for session', {
      labels: {
        sessionId: sessionId.substring(0, 8) + '...',
        status,
      },
    });
    return false;
  }

  if (!window.opener) {
    newTabLogger.warn('No opener window found for PostMessage');
    return false;
  }

  try {
    // The parent origin comes only from server-resolved session configuration.
    const sessionState = sessionStateManager.getState();
    const returnUrl = sessionState?.config?.returnUrl || undefined;

    if (!returnUrl) {
      newTabLogger.error('No returnUrl for PostMessage origin');
      return false;
    }

    // Get externalUserId from parameter or session state
    let effectiveExternalUserId = externalUserId;
    if (!effectiveExternalUserId) {
      const sessionState = sessionStateManager.getState();
      effectiveExternalUserId = sessionState?.config?.externalUserId || undefined;
    }

    const targetOrigin = new URL(returnUrl).origin;
    const runtimeBrand = runtimeConfig.isLoaded()
      ? runtimeConfig.getConfig().brand?.brandSlug
      : undefined;
    const sessionBrand = getSessionState()?.config?.brand?.brandSlug;
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const urlBrand = isLocalhost
      ? new URLSearchParams(window.location.search).get('devBrand')
      : null;
    const devBrand = isLocalhost ? getDevBrandSlug() : null;
    let hostnameBrand: string | undefined;
    if (hostname.includes('privateav')) {
      hostnameBrand = 'privateav';
    } else if (hostname.includes('safepassage')) {
      hostnameBrand = 'safepassage';
    }
    const brandSlug =
      runtimeBrand || sessionBrand || urlBrand || devBrand || hostnameBrand;
    if (!brandSlug) {
      newTabLogger.warn('No brand slug resolved for PostMessage', {
        labels: {
          runtimeBrand: runtimeBrand || 'none',
          sessionBrand: sessionBrand || 'none',
          urlBrand: urlBrand || 'none',
          devBrand: devBrand || 'none',
          hostname,
        }
      });
      return false;
    }
    const message: {
      type: string;
      sessionId: string;
      status: 'verified' | 'failed' | 'cancelled';
      timestamp: number;
      externalUserId?: string;
    } = {
      type: `${brandSlug}:verification:complete`,
      sessionId,
      status,
      timestamp: Date.now(),
    };

    if (effectiveExternalUserId) {
      message.externalUserId = effectiveExternalUserId;
    }

    newTabLogger.info('Sending verification result to parent', {
      labels: {
        targetOrigin,
        status,
        sessionId: sessionId.substring(0, 8) + '...',
      }
    });

    window.opener.postMessage(message, targetOrigin);
    return true;
  } catch (error) {
    newTabLogger.error('Failed to send PostMessage:', { error: error instanceof Error ? error : new Error(`${error}`) });
    return false;
  }
}

/**
 * Attempt to close the current tab/window
 */
export function attemptCloseTab(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      // Some browsers don't allow window.close() unless the window was opened by script
      // We'll try it and check if we're still open after a delay
      window.close();

      // Check if close worked after a short delay
      setTimeout(() => {
        // If this code runs, the window didn't close
        newTabLogger.info('Window.close() did not close the tab');
        resolve(false);
      }, 100);
    } catch (error) {
      newTabLogger.warn('Failed to close tab:', { error: error instanceof Error ? error : new Error(`${error}`) });
      resolve(false);
    }
  });
}

/**
 * Handle completion in new-tab mode
 * Sends result to parent and attempts to close tab
 */
export async function handleNewTabCompletion(
  sessionId: string | undefined,
  status: 'verified' | 'failed',
  externalUserId?: string
): Promise<{ messageSent: boolean; tabClosed: boolean }> {
  const messageSent = sendVerificationResult(sessionId, status, externalUserId);
  if (messageSent && sessionId) {
    markNewTabResultSent(sessionId);
  }

  // Wait a bit to ensure message is sent before attempting to close
  await new Promise((resolve) => setTimeout(resolve, 250));

  const tabClosed = await attemptCloseTab();

  return { messageSent, tabClosed };
}
